/**
 * The `landing` namespace, in English — the public page at `/`.
 *
 * It is the only namespace a visitor can reach with no session and no invitation, which is
 * what makes it different from `entrance`: those screens address somebody who already knows
 * what Kalqa is, this one addresses somebody who does not. Hence plain sentences about the
 * work rather than the product's own vocabulary — a page that opens with "version slot" has
 * lost the reader it was written for.
 *
 * TERMINOLOGY (chat1.md §6) still binds where a term does appear: a drawing is a drawing,
 * a review is a review, and the thing we email is a **Sign-in link** — never the backend's
 * internal name, which `scripts/guard.mjs` fails the build on.
 *
 * LENGTH: `scripts/check-expansion.mjs` measures every key here pseudo-expanded by 1.3×.
 * The headings are the tight slot (`*.title` — 78 characters after expansion, so ~60 in
 * English); the body copy is measured against the wrapping-prose budget.
 */

export const landingEn = {
  // TWIN: `brand.wordmark` in `./entrance.ts` and `./setup.ts`. Deliberately duplicated so a
  // language pack can ship one screen family without the others (retro rule 12) — change it
  // here and it changes there too.
  'brand.wordmark': 'KALQA',

  'language.label': 'Language',

  'nav.signIn': 'Sign in',
  'nav.signUp': 'Create account',

  'hero.title': 'One drawing set. One version of it.',
  'hero.body':
    'Kalqa keeps the drawings, the review comments and the tasks of a construction project in one place, so the sheet in the contractor’s hand is the sheet you published.',
  'hero.action.primary': 'Create a free account',
  'hero.action.secondary': 'Sign in',
  'hero.note': 'Free while Kalqa is in beta. No card, no sales call.',

  'features.title': 'What it replaces',
  'features.lede':
    'A shared drive of PDFs, a mail thread per remark, and a spreadsheet nobody but its author can read.',

  'feature.versions.title': 'Versions that stay in step',
  'feature.versions.body':
    'Every sheet has one current version and its whole history behind it. Superseded drawings stay readable, and nobody has to guess which file is the latest.',

  'feature.reviews.title': 'Comments on the sheet',
  'feature.reviews.body':
    'A remark is pinned to the drawing it concerns, with an author and a status, instead of being scattered across three mail threads and a phone call.',

  'feature.tasks.title': 'Tasks with an owner',
  'feature.tasks.body':
    'Any remark becomes a task with a person and a date on it, visible to everybody working on the project rather than to whoever was copied in.',

  'feature.access.title': 'Everyone sees their own project',
  'feature.access.body':
    'Engineers, contractors and clients reach exactly what they were invited to. Your other projects are not merely hidden from them: they are out of reach.',

  'cta.title': 'Start with one project',
  'cta.body':
    'Set up your practice, upload a drawing set, invite the people who need it. Nothing to install.',
  'cta.action': 'Create a free account',

  'feature.register.title': 'A drawing register you can hand over',
  'feature.register.body':
    'Every sheet listed by phase, discipline and document type, with what changed since a date. Export it as CSV, or copy it straight into a spreadsheet.',

  'feature.share.title': 'One drawing for somebody without an account',
  'feature.share.body':
    'Send a client or a contractor a link to a single sheet or task. They see that one thing, under your practice’s name, and nothing else of yours.',

  // What is coming. Present tense above this line is reserved for what a studio can use today
  // (`MCK-20260521-001`, the launch line); everything here is worded as direction, and the
  // full roadmap with its stages lives behind a sign-in (`KLQ-20260904-003` §A). Nothing here
  // may promise a DWG preview or reading a model — `CLAUDE.md` hard rule.
  'roadmap.title': 'What is coming',
  'roadmap.lede': 'The order is set by the practices using Kalqa. No dates, on purpose.',

  'roadmap.next.label': 'Next',
  'roadmap.next.1': 'Issuing drawings with a record of who received which version, and when.',
  'roadmap.next.2':
    'An inbox across projects: what is assigned to you, what needs your review, and an email only when it matters.',
  'roadmap.next.3': 'Comparing two versions side by side, with the changes highlighted.',

  'roadmap.later.label': 'Later',
  'roadmap.later.1':
    'A desktop app that watches the folder your CAD publishes to, so nobody uploads anything.',
  'roadmap.later.2': 'Tasks exchanged with Archicad and Revit over BCF.',
  'roadmap.later.3': 'A mobile view for the site, with the current drawings available offline.',
  'roadmap.later.4': 'Handover of the finished building as COBie sheets.',

  'footer.help': 'Help',
  'footer.imprint': 'Legal notice',
  'footer.terms': 'Terms',
  'footer.privacy': 'Privacy',
  'footer.copyright': '© {year} KALQA',
} as const;
